import { useCallback } from "react";
import { useNavigate } from "react-router";
import type {
  DatalakeOptions,
  DatalakeResource,
  DatalakeUpdateOptions,
  GetDatalakeResponse,
  ListDatalakesResponse,
  TestDatalakeResponse,
} from "@generated/import-openapi.gen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { PaginatedResponse, PaginationParams } from "@prequel/import-react";

import { importClient } from "./sdk/client";

import type { TestConnectionResult } from "@/api/client";
import { type PageOptions, useAllPages, usePage } from "@/api/pagination";
import { useToast } from "@/providers/ToastProvider";

export type { DatalakeOptions, DatalakeResource, DatalakeUpdateOptions } from "@generated/import-openapi.gen";

const routes = {
  list: "/import/datalakes",
  get: (id: string) => `/import/datalakes/${id}`,
  create: "/import/datalakes",
  update: (id: string) => `/import/datalakes/${id}`,
  delete: (id: string) => `/import/datalakes/${id}`,
  test: (id: string) => `/import/datalakes/${id}/test`,
};

const keys = { all: ["import", "datalakes"] as const };

export type DatalakeFilters = {
  storage?: string;
};

export const listDatalakesPage = (
  params: PaginationParams,
  filters?: DatalakeFilters,
): Promise<PaginatedResponse<DatalakeResource[]>> =>
  importClient.getPaginated<ListDatalakesResponse>(routes.list, params, filters).then((res) => ({
    data: res.data.datalakes,
    has_next: res.has_next,
    next_url: res.next_url,
  }));

export function useListDatalakes(options?: PageOptions, filters?: DatalakeFilters) {
  const storage = filters?.storage;

  const fetchPage = useCallback((params: PaginationParams) => listDatalakesPage(params, { storage }), [storage]);

  return usePage<DatalakeResource>([...keys.all, { storage }], fetchPage, options);
}

export function useAllDatalakes() {
  return useAllPages<DatalakeResource>([...keys.all], listDatalakesPage);
}

export function useDatalake(id: string) {
  return useQuery({
    queryKey: [...keys.all, id],
    queryFn: () => importClient.get<GetDatalakeResponse>(routes.get(id)).then((data) => data.datalake),
  });
}

export function useCreateDatalake() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (datalake: DatalakeOptions) =>
      importClient.post<GetDatalakeResponse>(routes.create, { datalake }).then((data) => data.datalake),
    onSuccess: (datalake) => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Datalake created successfully!" });
      navigate(`/import/datalakes/${datalake.id}`);
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}

export function useUpdateDatalake() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ id, datalake }: { id: string; datalake: DatalakeUpdateOptions }) =>
      importClient.patch<GetDatalakeResponse>(routes.update(id), { datalake }).then((data) => data.datalake),
    onSuccess: (datalake) => {
      queryClient.setQueryData([...keys.all, datalake.id], datalake);
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Datalake updated successfully!" });
      navigate(`/import/datalakes/${datalake.id}`);
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}

export function useDeleteDatalake() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => importClient.delete(routes.delete(id)),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [...keys.all, id] });
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Datalake deleted successfully!" });
      navigate("/import/datalakes");
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}

/** Runs the datalake connection diagnostic, which reports a failure in-band as a code and message. */
export function useTestDatalake() {
  return useMutation({
    mutationFn: (id: string): Promise<TestConnectionResult> =>
      importClient
        .post<TestDatalakeResponse>(routes.test(id))
        .then((data): TestConnectionResult =>
          data.code || data.message ? { status: "error", message: data.message } : { status: "success" },
        ),
  });
}
